import { HALL_EVENT_PAYMENT_METHOD_OPTIONS } from './hallEventForm';

export function roundHallMoney(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return 0;
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

export function formatHallCurrency(value) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0) || 0);
}

export function paymentModeLabel(value) {
  const match = HALL_EVENT_PAYMENT_METHOD_OPTIONS.find(
    (option) => option.value === String(value || '').toLowerCase(),
  );
  return match?.label || String(value || '—').replace(/_/g, ' ').toUpperCase();
}

export function paymentStatusMeta(status, isDark = false) {
  const key = String(status || '').toLowerCase();
  if (key === 'paid') {
    return {
      label: 'Fully Paid',
      color: isDark ? '#bbf7d0' : '#166534',
      bg: isDark ? 'rgba(34,197,94,0.22)' : 'rgba(34,197,94,0.14)',
    };
  }
  if (key === 'partial') {
    return {
      label: 'Partially Paid',
      color: isDark ? '#fde68a' : '#92400e',
      bg: isDark ? 'rgba(245,158,11,0.22)' : 'rgba(245,158,11,0.14)',
    };
  }
  return {
    label: 'Unpaid',
    color: isDark ? '#fecaca' : '#991b1b',
    bg: isDark ? 'rgba(239,68,68,0.22)' : 'rgba(239,68,68,0.12)',
  };
}

export function computeChargePreview(baseAmount, gstPercent = 0, applyGst = true) {
  const base = roundHallMoney(baseAmount);
  const percent = applyGst ? Math.max(0, Math.min(100, roundHallMoney(gstPercent))) : 0;
  const gstAmount = roundHallMoney((base * percent) / 100);
  return {
    base_amount: base,
    gst_percent: percent,
    gst_amount: gstAmount,
    total: roundHallMoney(base + gstAmount),
  };
}

export function resolveLocalDiscountAmount(discount, baseForPercent) {
  const type = String(discount?.type || 'amount').toLowerCase();
  const value = roundHallMoney(discount?.value != null ? discount.value : discount?.amount);
  if (type === 'percentage') {
    return roundHallMoney((Number(baseForPercent || 0) * Math.min(100, Math.max(0, value))) / 100);
  }
  return Math.max(0, value);
}

/** Recalculate payment summary from local extras/discounts (live UI preview). */
export function computeHallLocalPaymentSummary(baseSummary, { extraCharges = [], discounts = [] } = {}) {
  const baseAmount = roundHallMoney(baseSummary?.base_amount);
  const extrasBase = roundHallMoney(
    (extraCharges || []).reduce((sum, item) => sum + (Number(item?.base_amount) || 0), 0),
  );
  const extrasGst = roundHallMoney(
    (extraCharges || []).reduce((sum, item) => sum + (Number(item?.gst_amount) || 0), 0),
  );
  const extrasTotal = roundHallMoney(
    (extraCharges || []).reduce((sum, item) => sum + (Number(item?.total) || 0), 0),
  );
  // Discounts apply on hall + extras base (not extras GST).
  const subtotalBeforeDiscount = roundHallMoney(baseAmount + extrasBase);

  let discountTotal = 0;
  (discounts || []).forEach((discount) => {
    const remaining = roundHallMoney(Math.max(0, subtotalBeforeDiscount - discountTotal));
    const amount = Math.min(resolveLocalDiscountAmount(discount, subtotalBeforeDiscount), remaining);
    discountTotal = roundHallMoney(discountTotal + amount);
  });

  const afterDiscount = roundHallMoney(Math.max(0, subtotalBeforeDiscount - discountTotal));
  // Event GST only on hall portion after discount; extras keep their own GST.
  const eventTaxable = roundHallMoney(Math.max(0, afterDiscount - extrasBase));
  const gstPercent = roundHallMoney(baseSummary?.gst_percent);
  const gstAmount = roundHallMoney((eventTaxable * gstPercent) / 100);
  const vendorIncludedTotal = roundHallMoney(baseSummary?.vendor_included_total);
  const vendorPaidTotal = roundHallMoney(baseSummary?.vendor_paid_total);
  const grandTotal = roundHallMoney(afterDiscount + gstAmount + extrasGst + vendorIncludedTotal);
  const paidAmount = roundHallMoney(baseSummary?.paid_amount);
  const dueAmount = roundHallMoney(Math.max(0, grandTotal - paidAmount));
  const hotelNetCollection = roundHallMoney(paidAmount - vendorPaidTotal);

  let paymentStatus = 'unpaid';
  if (paidAmount > 0 && dueAmount <= 0) paymentStatus = 'paid';
  else if (paidAmount > 0) paymentStatus = 'partial';

  return {
    ...baseSummary,
    base_amount: baseAmount,
    extras_base_amount: extrasBase,
    extras_gst_amount: extrasGst,
    extras_total: extrasTotal,
    discount_amount: discountTotal,
    subtotal_before_discount: subtotalBeforeDiscount,
    subtotal_after_discount: afterDiscount,
    event_taxable_amount: eventTaxable,
    gst_percent: gstPercent,
    gst_amount: gstAmount,
    vendor_included_total: vendorIncludedTotal,
    vendor_paid_total: vendorPaidTotal,
    vendor_due_total: roundHallMoney(baseSummary?.vendor_due_total),
    vendor_contract_total: roundHallMoney(baseSummary?.vendor_contract_total),
    hotel_net_collection: hotelNetCollection,
    grand_total: grandTotal,
    paid_amount: paidAmount,
    due_amount: dueAmount,
    payment_status: paymentStatus,
    extra_charge_count: (extraCharges || []).length,
    discount_count: (discounts || []).length,
  };
}

export function resolveHallPaymentSummary(payload, booking) {
  const summary = payload?.payment_summary
    || booking?.payment_summary
    || {};
  const financialInfo = payload?.financial_info
    || booking?.financial_info
    || {};
  const payments = Array.isArray(payload?.payments)
    ? payload.payments
    : Array.isArray(booking?.payments)
      ? booking.payments
      : (Array.isArray(financialInfo.payments) ? financialInfo.payments : []);

  const baseAmount = Number(
    summary.base_amount != null ? summary.base_amount : booking?.amount,
  ) || 0;

  return {
    currency: summary.currency || 'INR',
    base_amount: baseAmount,
    extras_base_amount: Number(summary.extras_base_amount || 0) || 0,
    extras_gst_amount: Number(summary.extras_gst_amount || 0) || 0,
    extras_total: Number(summary.extras_total || 0) || 0,
    discount_amount: Number(summary.discount_amount || 0) || 0,
    subtotal_before_discount: Number(summary.subtotal_before_discount || baseAmount) || 0,
    subtotal_after_discount: Number(summary.subtotal_after_discount || baseAmount) || 0,
    event_taxable_amount: Number(summary.event_taxable_amount || 0) || 0,
    gst_percent: Number(summary.gst_percent != null ? summary.gst_percent : financialInfo.gst_percent) || 0,
    gst_amount: Number(summary.gst_amount || 0) || 0,
    gst_rule_id: summary.gst_rule_id ?? null,
    gst_rule_name: summary.gst_rule_name || '',
    gst_source: summary.gst_source || 'finance_setup',
    vendor_assignment_count: Number(summary.vendor_assignment_count || 0) || 0,
    vendor_contract_total: Number(summary.vendor_contract_total || 0) || 0,
    vendor_included_total: Number(summary.vendor_included_total || 0) || 0,
    vendor_excluded_total: Number(summary.vendor_excluded_total || 0) || 0,
    vendor_paid_total: Number(summary.vendor_paid_total || 0) || 0,
    vendor_due_total: Number(summary.vendor_due_total || 0) || 0,
    hotel_net_collection: Number(
      summary.hotel_net_collection != null
        ? summary.hotel_net_collection
        : (Number(summary.paid_amount || 0) || 0) - (Number(summary.vendor_paid_total || 0) || 0),
    ) || 0,
    grand_total: Number(summary.grand_total != null ? summary.grand_total : baseAmount) || 0,
    paid_amount: Number(summary.paid_amount || 0) || 0,
    due_amount: Number(
      summary.due_amount != null
        ? summary.due_amount
        : Math.max(0, (Number(summary.grand_total != null ? summary.grand_total : baseAmount) || 0)
          - (Number(summary.paid_amount || 0) || 0)),
    ) || 0,
    payment_status: summary.payment_status || 'unpaid',
    payment_count: Number(summary.payment_count || payments.length) || 0,
    extra_charge_count: Number(summary.extra_charge_count || (financialInfo.extra_charges || []).length) || 0,
    discount_count: Number(summary.discount_count || (financialInfo.discounts || []).length) || 0,
    financial_info: {
      gst_percent: Number(financialInfo.gst_percent || 0) || 0,
      extra_charges: Array.isArray(financialInfo.extra_charges) ? financialInfo.extra_charges : [],
      discounts: Array.isArray(financialInfo.discounts) ? financialInfo.discounts : [],
      payments,
      note: financialInfo.note || '',
    },
  };
}
